import React, {useCallback, useState} from "react";
import styled from "styled-components";
import {useDispatch, useSelector} from "react-redux";
import {
	getActivePot,
	getLiteSquadsByPots,
	getSelectedSquadId,
} from "apps/DRAW_PREDICTOR/modules/selectors";
import {Pots} from "apps/DRAW_PREDICTOR/modules/enums";
import {get} from "lodash";
import {LiteSquad} from "./LiteSquad";
import {
	clearPredictions,
	fetchPredictionSimulateLite,
	setActivePot,
} from "apps/DRAW_PREDICTOR/modules/actions";
import {media} from "assets/css/media";
import {LANGUAGE_PATH, trackAdobeAnalytics, useMediaQuery} from "modules";
import {PureButton} from "components";
import {ReactComponent as ChevronDown} from "assets/images/icons/chevron_down_black.svg";
import {useTranslation} from "react-i18next";
import {PotSelect} from "apps/DRAW_PREDICTOR/components/PotSelect";

const Wrapper = styled.div`
	position: sticky;
	bottom: 0;
	width: 100%;
	height: 105px;
	background: #ffffff;

	@media (max-width: ${media.mobile}) {
		height: auto;

		--defaultPaddingBottom: 0;
		padding-bottom: var(--defaultPaddingBottom);
		/* iOS Safari 11.2, Safari 11 */
		padding-bottom: calc(
			var(--defaultPaddingBottom) +
				constant(safe-area-inset-bottom, var(--defaultPaddingBottom))
		);
		/* iOS Safari 11.4+, Safari 11.1+, Chrome 69+, Opera 56+ */
		padding-bottom: calc(
			var(--defaultPaddingBottom) + env(safe-area-inset-bottom, var(--defaultPaddingBottom))
		);
	}
`;

const InnerWrapper = styled.div`
	display: flex;
	flex-wrap: wrap;
	justify-content: space-between;
	align-items: center;
	width: 100%;
	height: 100%;
	max-width: 1440px;
	margin: 0 auto;
	padding: 0 20px;
`;

const MobileInnerWrapper = styled.div`
	position: relative;
`;

interface ICountrySelectWrapperProps {
	isOpen: boolean;
}

const CountrySelectWrapper = styled.div<ICountrySelectWrapperProps>`
	position: absolute;
	top: 0;
	background: #ffffff;
	display: flex;
	flex-wrap: wrap;
	justify-content: space-between;
	align-items: center;
	padding: 16px;
	padding-bottom: 0;
	transition: transform 0.2s ease-in-out;
	transform: ${({isOpen}) => (isOpen ? "translateY(-100%)" : null)};
`;

const CountryList = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;

	@media (max-width: ${media.mobile}) {
		text-align: center;
		justify-content: flex-start;
		flex-wrap: wrap;
	}
`;

const ButtonStyled = styled.button`
	display: flex;
	flex-direction: row;
	justify-content: center;
	align-items: center;
	padding: 10px 30px;
	border: 2px solid #550065;
	box-sizing: border-box;
	border-radius: 50px;
	cursor: pointer;
	background: transparent;
	font-family: "Poppins", sans-serif;
	font-style: normal;
	font-weight: 500;
	font-size: 14px;
	text-transform: uppercase;
	color: #550065;

	&:hover {
		opacity: 0.8;
	}

	&:disabled {
		opacity: 0.2;
	}

	@media (max-width: ${media.mobile}) {
		padding: 0 18px;
		height: 32px;
		min-width: 100px;
	}
`;

const AutofillButton = styled(ButtonStyled)`
	background: #550065;
	border-color: #550065;
	color: #ffffff;
`;

const ButtonWrapper = styled.div`
	position: relative;
	z-index: 1;
	background: #ffffff;
	padding: 16px;
	display: flex;

	button:first-child {
		margin-inline-end: 20px;
	}

	@media (max-width: ${media.mobile}) {
		width: 100%;
		justify-content: space-between;
		align-items: center;
		height: 90px;
		button:first-child {
			margin-inline-end: 0;
		}
	}
`;

const PotSelectWrapper = styled.div`
	font-family: "Qatar2022", sans-serif;
`;

const SelectTeamTitle = styled.div`
	font-family: "Qatar2022", sans-serif;
	font-size: 16px;
	color: #000000;
	font-weight: 500;
	margin-bottom: 10px;
`;

const ToggleButton = styled(PureButton)<ICountrySelectWrapperProps>`
	position: absolute;
	z-index: 2;
	top: 0;
	right: 18px;
	transform: translateY(-50%);
	height: 40px;
	width: 40px;
	background-color: #fff;
	border-radius: 50%;
	display: flex;
	justify-content: center;
	align-items: center;

	svg {
		transition: transform 0.2s ease-in-out;
		transform: ${({isOpen}) => (!isOpen ? "rotateZ(180deg)" : null)};
	}
`;
export const LiteSquadSelect: React.FC = () => {
	const dispatch = useDispatch();
	const isMobile = useMediaQuery(`(max-width: ${media.mobile})`);
	const [isSelectorOpen, setIsSelectorOpen] = useState(true);
	const activePot = useSelector(getActivePot);
	const pots = useSelector(getLiteSquadsByPots);
	const selectedSquadId = useSelector(getSelectedSquadId);
	const selectedSquads = get(pots, activePot, pots[Pots.POT1]);
	const {t} = useTranslation(LANGUAGE_PATH);
	const potTitle = t("draw.pot.selector");

	const handleReset = useCallback(() => {
		dispatch(clearPredictions());
		trackAdobeAnalytics({
			section: "",
			eventName: "drawReset",
		});
	}, [dispatch]);

	const handleChangePot = useCallback(
		(e: React.SyntheticEvent<HTMLSelectElement>) => {
			const {value} = e.currentTarget;
			dispatch(setActivePot(parseInt(value)));
		},
		[dispatch]
	);

	const handleSimulateDraw = useCallback(() => {
		dispatch(fetchPredictionSimulateLite());
		setIsSelectorOpen(false);

		trackAdobeAnalytics({
			section: "",
			eventName: "drawAutoFill",
		});
	}, [dispatch]);

	const toggleSelector = () => {
		setIsSelectorOpen(!isSelectorOpen);
	};

	if (isMobile) {
		return (
			<Wrapper className="draw-second-step">
				<MobileInnerWrapper>
					<CountrySelectWrapper isOpen={isSelectorOpen}>
						<ToggleButton isOpen={isSelectorOpen} onClick={toggleSelector}>
							<ChevronDown width="16" height="9" />
						</ToggleButton>
						<PotSelectWrapper>
							<SelectTeamTitle>{t("mobile.pot.select_team")}</SelectTeamTitle>
						</PotSelectWrapper>

						<CountryList className="draw-third-step">
							{selectedSquads.map((squad, index) => (
								<LiteSquad squad={squad} key={index} />
							))}
						</CountryList>
					</CountrySelectWrapper>
					<ButtonWrapper>
						<PotSelect
							onChange={handleChangePot}
							disabled={Boolean(selectedSquadId)}
							value={activePot}
							title={potTitle}
						/>
						<AutofillButton onClick={handleSimulateDraw}>
							{t("draw.button.autofill")}
						</AutofillButton>
						<ButtonStyled onClick={handleReset}>
							{t("draw.pot.button_reset")}
						</ButtonStyled>
					</ButtonWrapper>
				</MobileInnerWrapper>
			</Wrapper>
		);
	}

	return (
		<Wrapper className="draw-second-step">
			<InnerWrapper>
				<PotSelectWrapper>
					<SelectTeamTitle>{t("mobile.pot.select_team")}</SelectTeamTitle>
					<PotSelect
						onChange={handleChangePot}
						disabled={Boolean(selectedSquadId)}
						value={activePot}
						title={potTitle}
					/>
				</PotSelectWrapper>

				<CountryList className="draw-third-step">
					{selectedSquads.map((squad, index) => (
						<LiteSquad squad={squad} key={index} />
					))}
				</CountryList>
				<ButtonWrapper>
					<AutofillButton onClick={handleSimulateDraw}>
						{t("draw.button.autofill")}
					</AutofillButton>
					<ButtonStyled onClick={handleReset}>{t("draw.pot.button_reset")}</ButtonStyled>
				</ButtonWrapper>
			</InnerWrapper>
		</Wrapper>
	);
};
